import { TICK_RATE, Team, otherTeam } from "../constants.ts";
import { GameMode } from "../map/maps.ts";
import { type MapData, type Track, type TrackPose, trackPose } from "../map/mapdata.ts";
import type { Obstacle } from "../sim/physics.ts";
import type { Sim } from "../sim/sim.ts";
import { CartState, ControlMatch, DEFAULT_MATCH_SETTINGS, MatchRules, type MatchSettings, type MatchState, Phase, ti } from "./match.ts";

/** The payload: a flatbed chassis you can hop onto, carrying a core that's cover when you stand. */
export const CART = {
  /** Chassis half length / half width / height. */
  hl: 1.8,
  hw: 1.2,
  deck: 0.95,
  /** Core, standing on the deck. */
  coreHl: 0.95,
  coreHw: 0.8,
  coreH: 1.0,
  /** Players within this (horizontal) distance of its centre push or contest it. */
  radius: 4.5,
} as const;

/** Push speed in mm per tick with 1, 2, 3+ attackers beside it: ~1.0, 1.2, 1.3 m/s. */
const PUSH_MM = [0, 17, 20, 22] as const;
/** Rollback speed: ~0.4 m/s, never behind the last checkpoint. */
const ROLLBACK_MM = 7;
/** Rollback starts after this long without an attacker beside the payload. */
const ROLLBACK_DELAY = 10 * TICK_RATE;
/** Overtime lasts while attackers touch the payload, and this long after the last one leaves. */
const FUSE_TICKS = Math.round(2.5 * TICK_RATE);

export const cartObstacles = (p: TrackPose): Obstacle[] => [
  { x: p.x, y: p.y, z: p.z, fx: p.fx, fz: p.fz, hl: CART.hl, hw: CART.hw, h: CART.deck },
  { x: p.x, y: p.y + CART.deck, z: p.z, fx: p.fx, fz: p.fz, hl: CART.coreHl, hw: CART.coreHw, h: CART.coreH },
];

const boxObstacle = (b: { min: { x: number; y: number; z: number }; max: { x: number; y: number; z: number } }): Obstacle => ({
  x: (b.min.x + b.max.x) / 2,
  y: b.min.y,
  z: (b.min.z + b.max.z) / 2,
  fx: 1,
  fz: 0,
  hl: (b.max.x - b.min.x) / 2,
  hw: (b.max.z - b.min.z) / 2,
  h: b.max.y - b.min.y,
});

/** A side's spawn stage once `checkpoint` checkpoints have fallen: its latest stage not beyond that. */
export function stageFor(map: MapData, side: Team, checkpoint: number): number {
  let stage = 0;
  for (const s of map.spawns) if (s.team === side && s.stage <= checkpoint && s.stage > stage) stage = s.stage;
  return stage;
}

const sameSide = (t: Team) => t;
const swapSides = (t: Team) => otherTeam(t);

/** The parts of the match state that shape the world (`stage`: Control's, 0 if left out). */
export type MatchWorld = Pick<MatchState, "mode" | "phase" | "attacker" | "cartMm" | "checkpoint"> & { stage?: number };

/**
 * Applies the match to the world: payload obstacles, closed spawn gates during Setup, which map
 * side each team spawns on and which spawn stage is in use (Convoy: by checkpoint; Control: the
 * stage in play). The server calls it as the match changes; client prediction calls it with each
 * snapshot's (exactly replicated) values, so both collide with bit-identical obstacles.
 */
export function convoyWorld(sim: Sim, m: MatchWorld) {
  const track = sim.map.track;
  if (m.mode !== GameMode.Convoy || !track) {
    sim.cart = undefined;
    if (sim.baseObstacles.length) sim.baseObstacles = [];
    sim.refreshObstacles();
    sim.spawnSide = sameSide;
    const stage = m.mode === GameMode.Control ? (m.stage ?? 0) : 0;
    sim.spawnStage = [stage, stage];
    return;
  }
  sim.spawnSide = m.attacker === Team.B ? swapSides : sameSide;
  sim.spawnStage = [stageFor(sim.map, Team.A, m.checkpoint), stageFor(sim.map, Team.B, m.checkpoint)];
  const pose = trackPose(track, m.cartMm / 1000);
  sim.cart = pose;
  const obstacles = cartObstacles(pose);
  if (m.phase === Phase.Setup) for (const g of sim.map.gates) if (g.team === Team.A && g.stage === sim.spawnStage[0]) obstacles.push(boxObstacle(g));
  sim.baseObstacles = obstacles;
  sim.refreshObstacles();
}

interface Attack {
  delivered: boolean;
  cartMm: number;
  /** Attack ticks left when it ended (delivery time bank). */
  left: number;
}

/**
 * Convoy (escort) rules, after Marvel Rivals. Two rounds: team A attacks, then team B. Attackers
 * beside the payload push it (faster with more of them, up to three); any defender beside it
 * stops it; left alone for 10 s it rolls back, but never past a checkpoint. Checkpoints add
 * attack time and move both teams' spawns. When the clock runs out, overtime lasts while
 * attackers touch the payload. The team that pushed further wins; if both deliver, the one with
 * more time left. Round two ends the moment it's decided.
 */
export class ConvoyMatch extends MatchRules {
  readonly track: Track;
  readonly lengthMm: number;
  private idle = 0;
  private fuse = FUSE_TICKS;
  /** Each team's attack, by team index. */
  private attacks: (Attack | undefined)[] = [undefined, undefined];

  constructor(sim: Sim, settings: MatchSettings = DEFAULT_MATCH_SETTINGS) {
    super(sim, settings, GameMode.Convoy);
    if (!sim.map.track) throw new Error("Convoy needs a map with a TRACK_* route");
    this.track = sim.map.track;
    this.lengthMm = Math.floor(this.track.length * 1000);
    this.sync();
  }

  private ticksFor(seconds: number) {
    return Math.round(seconds * this.settings.convoyTime * TICK_RATE);
  }

  private checkpointMm(i: number) {
    return Math.ceil(this.track.checkpoints[i]!.dist * 1000);
  }

  /** Rollback floor: the last checkpoint passed. */
  private floorMm() {
    return this.state.checkpoint > 0 ? this.checkpointMm(this.state.checkpoint - 1) : 0;
  }

  /** Attack time the checkpoints still ahead would add. */
  private bonusAhead() {
    let t = 0;
    for (let i = this.state.checkpoint; i < this.track.checkpoints.length; i++) t += this.ticksFor(this.track.checkpoints[i]!.bonus);
    return t;
  }

  private sync() {
    convoyWorld(this.sim, this.state);
  }

  protected enterWarmup() {
    this.attacks = [undefined, undefined];
    this.sync();
  }

  protected startRound() {
    const s = this.state;
    s.round++;
    s.attacker = s.round % 2 === 1 ? Team.A : Team.B;
    s.cartMm = 0;
    s.checkpoint = 0;
    s.clock = this.ticksFor(this.track.startSeconds);
    s.overtime = false;
    s.contested = false;
    s.cartState = CartState.Idle;
    s.winner = Team.None;
    s.progress[ti(s.attacker)] = 0;
    s.score[ti(s.attacker)] = 0;
    const first = this.attacks[ti(otherTeam(s.attacker))];
    s.goalDone = !!first?.delivered;
    s.goalTicks = first?.delivered ? first.left : 0;
    this.idle = 0;
    this.fuse = FUSE_TICKS;
    this.setPhase(Phase.Setup, this.settings.convoySetupSeconds);
    this.sync();
    // Everyone back to (the new round's) spawn, fresh.
    for (const eid of this.sim.playerEids()) this.sim.respawn(eid);
    for (const hp of this.sim.healthPacks) hp.cooldown = 0;
  }

  protected unlock() {
    super.unlock();
    this.sync();
  }

  protected countObjective(): [number, number] {
    const p = this.sim.cart;
    if (!p) return [0, 0];
    const r2 = CART.radius * CART.radius;
    return this.countIn((x, y, z) => {
      const dx = x - p.x;
      const dz = z - p.z;
      const dy = y - p.y;
      return dx * dx + dz * dz <= r2 && dy > -1 && dy < 4;
    });
  }

  protected tickObjective(on: [number, number]) {
    const s = this.state;
    const ai = ti(s.attacker);
    const atk = on[ai];
    const def = on[1 - ai];
    s.contested = atk > 0 && def > 0;
    this.idle = atk > 0 ? 0 : this.idle + 1;
    if (atk > 0 && def === 0) {
      s.cartMm = Math.min(this.lengthMm, s.cartMm + PUSH_MM[Math.min(3, atk)]!);
      s.cartState = CartState.Pushing;
    } else if (atk > 0) s.cartState = CartState.Contested;
    else if (this.idle > ROLLBACK_DELAY && s.cartMm > this.floorMm()) {
      s.cartMm = Math.max(this.floorMm(), s.cartMm - ROLLBACK_MM);
      s.cartState = CartState.Rolling;
    } else s.cartState = def > 0 ? CartState.Blocked : CartState.Idle;

    while (s.checkpoint < this.track.checkpoints.length && s.cartMm >= this.checkpointMm(s.checkpoint)) {
      s.clock += this.ticksFor(this.track.checkpoints[s.checkpoint]!.bonus);
      s.checkpoint++;
      s.overtime = false;
      this.fuse = FUSE_TICKS;
    }
    const delivered = s.cartMm >= this.lengthMm;
    s.progress[ai] = (100 * s.cartMm) / this.lengthMm;
    s.score[ai] = s.checkpoint + (delivered ? 1 : 0);
    this.sync();
    if (delivered) return this.endAttack(true);

    if (s.clock > 0) s.clock--;
    if (s.clock === 0) {
      if (atk > 0) {
        s.overtime = true;
        this.fuse = FUSE_TICKS;
      } else if (!s.overtime || --this.fuse <= 0) return this.endAttack(false);
    } else s.overtime = false;

    // Round two stops as soon as the result can't change.
    const first = this.attacks[1 - ai];
    if (!first) return;
    if (!first.delivered && s.cartMm > first.cartMm) return this.endAttack(false);
    if (first.delivered && s.clock + this.bonusAhead() < first.left) return this.endAttack(false);
  }

  private endAttack(delivered: boolean) {
    const s = this.state;
    this.attacks[ti(s.attacker)] = { delivered, cartMm: s.cartMm, left: s.clock };
    s.overtime = false;
    s.contested = false;
    s.cartState = CartState.Idle;
    s.winner = this.attacks[0] && this.attacks[1] ? this.decide(this.attacks[0], this.attacks[1]) : Team.None;
    this.setPhase(Phase.RoundEnd, this.settings.roundEndSeconds);
  }

  /** The result once both teams have attacked; None is a draw. */
  private decide(a: Attack, b: Attack): Team {
    if (a.delivered && b.delivered) return a.left > b.left ? Team.A : b.left > a.left ? Team.B : Team.None;
    if (a.delivered !== b.delivered) return a.delivered ? Team.A : Team.B;
    return a.cartMm > b.cartMm ? Team.A : b.cartMm > a.cartMm ? Team.B : Team.None;
  }

  protected matchOver() {
    return !!(this.attacks[0] && this.attacks[1]);
  }
}

export type Match = ControlMatch | ConvoyMatch;

export function createMatch(mode: GameMode, sim: Sim, settings: MatchSettings = DEFAULT_MATCH_SETTINGS): Match {
  return mode === GameMode.Convoy ? new ConvoyMatch(sim, settings) : new ControlMatch(sim, settings);
}
